import {
  currentEnvironment,
  ENVIRONMENTS,
  isPreviewMode,
} from './runtime-config';

// AEM Cloud Flag - determines whether to use cloud or classical domains
const AEM_CLOUD_FLAG =
  process.env.NEXT_PUBLIC_AEM_CLOUD_FLAG?.toLowerCase() === 'true';

/**
 * Determines if AEM Cloud domains should be used
 * Based on NEXT_PUBLIC_AEM_CLOUD_FLAG environment variable
 */
const getAEMCloudFlag = (): boolean => {
  return AEM_CLOUD_FLAG;
};

// AEM Domain Configurations
interface AEMConfig {
  main: string;
  preview: string;
  previewAsset: string;
  publish?: string;
}

/**
 * Gets the path for AEM Cloud environments
 */
function getAEMCloudPath(environment: string): AEMConfig {
  switch (environment) {
    case 'test1':
    case 'test2':
    case 'test3':
    case 'test4':
    case 'test5': // needs to point to aem cloud dev
      return {
        main: 'https://stg-publish.virginatlantic.com/',
        preview:
          'https://author-p166388-e1796209.adobeaemcloud.com/bin/vaahome/AEMPreviewDataExporter?brandPath=',
        previewAsset:
          'https://author-p166388-e1796209.adobeaemcloud.com/bin/vaahome/AEMPreviewDataExporter?assetPath=',
      };
    case 'uat': // needs to point to aem cloud staging
      return {
        main: 'https://stg-vaabrowse.virginatlantic.com/',
        preview:
          'https://author-virginatlantic-s5b.adobecqms.net/bin/vaahome/AEMPreviewDataExporter?brandPath=',
        previewAsset:
          'https://author-virginatlantic-s5b.adobecqms.net/bin/vaahome/AEMPreviewDataExporter?assetPath=',
      };
    case 'prod':
      return {
        main: 'https://vaabrowse.virginatlantic.com/',
        preview:
          'https://viv-prod.virginatlantic.com/bin/vaahome/AEMPreviewDataExporter?brandPath=',
        previewAsset:
          'https://viv-prod.virginatlantic.com/bin/vaahome/AEMPreviewDataExporter?assetPath=',
        publish: 'https://www.virginatlantic.com',
      };
    case 'local':
      return {
        main: 'http://localhost:4502/',
        preview: 'http://localhost:4502?brandPath=',
        previewAsset:
          'http://localhost:4502/bin/vaahome/AEMPreviewDataExporter?assetPath=',
      };
    default:
      throw new Error(`AEM Cloud environment '${environment}' not supported`);
  }
}

/**
 * Gets the path for classic AEM environments
 */
function getAEMClassicPath(environment: string): AEMConfig {
  const env = environment.toLowerCase();

  switch (environment) {
    case 'test1':
    case 'test2':
    case 'test3':
    case 'test4':
    case 'test5':
      return {
        main: `https://${env}-vaahome.webdev.vholsinternal.co.uk/`,
        preview: `https://${env}-viv.webdev.vholsinternal.co.uk/bin/vaahome/AEMPreviewDataExporter?brandPath=`,
        previewAsset: `https://${env}-viv.webdev.vholsinternal.co.uk/bin/vaahome/AEMPreviewDataExporter?assetPath=`,
      };
    case 'uat':
      return {
        main: 'https://stg-vaabrowse.virginatlantic.com/',
        preview:
          'https://author-virginatlantic-s5b.adobecqms.net/bin/vaahome/AEMPreviewDataExporter?brandPath=',
        previewAsset:
          'https://author-virginatlantic-s5b.adobecqms.net/bin/vaahome/AEMPreviewDataExporter?assetPath=',
      };
    case 'prod':
      return {
        main: 'https://vaabrowse.virginatlantic.com/',
        preview:
          'https://viv-prod.virginatlantic.com/bin/vaahome/AEMPreviewDataExporter?brandPath=',
        previewAsset:
          'https://viv-prod.virginatlantic.com/bin/vaahome/AEMPreviewDataExporter?assetPath=',
        publish: 'https://www.virginatlantic.com',
      };
    case 'local':
      return {
        main: 'http://localhost:4502/',
        preview: 'http://localhost:4502?brandPath=',
        previewAsset:
          'http://localhost:4502/bin/vaahome/AEMPreviewDataExporter?assetPath=',
      };
    default:
      throw new Error(`Invalid environment '${environment}'`);
  }
}

type Endpoints = {
  FEDERATED_DATA_API: { ABS: string; REL: string };
  LOCATIONS_API: string;
};

export const ENDPOINTS = {
  FEDERATED_DATA_API: {
    PATH: '/api/federated-data-api',
    PROD: 'https://www.virginatlantic.com/federated-data-api',
  },
  LOCATIONS_API: {
    PROD: 'https://www.virginatlantic.com/locations-public-api',
    TEST1:
      'http://vsolpublic-test1.webdev.vholsinternal.co.uk/locations-public-api',
  },
};

export const getEndpoint = (origin: string = ''): Endpoints => {
  let endpoints: Endpoints = {
    FEDERATED_DATA_API: { ABS: '', REL: '' },
    LOCATIONS_API: '',
  };
  switch (currentEnvironment) {
    case ENVIRONMENTS.LOCAL:
      switch (isPreviewMode) {
        case true:
          endpoints = {
            FEDERATED_DATA_API: {
              ABS: `${origin}/preview${ENDPOINTS.FEDERATED_DATA_API.PATH}`,
              REL: ENDPOINTS.FEDERATED_DATA_API.PATH,
            },
            LOCATIONS_API: `${origin}/preview${ENDPOINTS.LOCATIONS_API.PROD}`,
          };
          break;
        case false:
          endpoints = {
            FEDERATED_DATA_API: {
              ABS: `http:localhost:3000${ENDPOINTS.FEDERATED_DATA_API.PATH}`,
              REL: ENDPOINTS.FEDERATED_DATA_API.PATH,
            },
            LOCATIONS_API: `${origin}${ENDPOINTS.LOCATIONS_API.PROD}`,
          };
          break;
        default:
          break;
      }
      break;
    case ENVIRONMENTS.TEST1:
    case ENVIRONMENTS.TEST2:
    case ENVIRONMENTS.TEST3:
    case ENVIRONMENTS.TEST4:
    case ENVIRONMENTS.TEST5:
    case ENVIRONMENTS.UAT:
    case ENVIRONMENTS.PROD:
      switch (isPreviewMode) {
        case true:
          endpoints = {
            FEDERATED_DATA_API: {
              REL: `/preview${ENDPOINTS.FEDERATED_DATA_API.PATH}`,
              ABS: ENDPOINTS.FEDERATED_DATA_API.PROD,
            },
            LOCATIONS_API: ENDPOINTS.LOCATIONS_API.PROD,
          };
          break;
        case false:
          endpoints = {
            FEDERATED_DATA_API: {
              REL: ENDPOINTS.FEDERATED_DATA_API.PATH,
              ABS: ENDPOINTS.FEDERATED_DATA_API.PROD,
            },
            LOCATIONS_API: ENDPOINTS.LOCATIONS_API.PROD,
          };
          break;
        default:
          break;
      }
      break;
    default:
      endpoints = {
        FEDERATED_DATA_API: {
          REL: ENDPOINTS.FEDERATED_DATA_API.PATH,
          ABS: ENDPOINTS.FEDERATED_DATA_API.PROD,
        },
        LOCATIONS_API: ENDPOINTS.LOCATIONS_API.PROD,
      };
  }
  return endpoints;
};

/**
 * Configuration for AEM (Adobe Experience Manager) endpoints
 * Returns the correct URLs based on environment and NEXT_PUBLIC_AEM_CLOUD_FLAG
 */
export function getAEMBasepath(): AEMConfig {
  const environmentKey = currentEnvironment;
  const isCloudEnabled = getAEMCloudFlag();

  try {
    if (isCloudEnabled) {
      return getAEMCloudPath(environmentKey);
    } else {
      return getAEMClassicPath(environmentKey);
    }
  } catch (error) {
    console.warn(`${error}. Using production classical fallback config.`);
    return getAEMClassicPath('prod');
  }
}

/**
 * Returns the hostname URL based on the current environment
 * Used for generating canonical URLs and redirects
 */
export const getHostName = (): string => {
  const env = currentEnvironment;

  // Test environments: test1 through test5
  if (env.startsWith('test')) {
    return `https://vaa-${env}.virginatlantic.com`;
  }

  // UAT/Staging environments
  if (env === 'uat') {
    return 'https://stg.virginatlantic.com';
  }

  // Production environment
  if (env === 'prod') {
    return 'https://www.virginatlantic.com';
  }

  // Local development
  if (env === 'local') {
    return 'http://localhost:3000';
  }

  // Default fallback to production
  console.warn(`Unknown environment: ${env}. Using production hostname.`);
  return 'https://www.virginatlantic.com';
};
